import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, Typography, Snackbar, Alert,
  Paper, Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Switch as MuiSwitch } from "@mui/material";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminGridToolbar from "../../components/admin/AdminGridToolbar";
import AdminFilterPanel, { filterFieldSx } from "../../components/admin/AdminFilterPanel";
import AdminFormDialog from "../../components/admin/AdminFormDialog";
import { listCompanies, createCompany, updateCompany, deleteCompany } from "../../services/adminService";

const GRID_SX = {
  border: 0,
  "& .MuiDataGrid-columnHeaders": { backgroundColor: "rgba(0,0,0,0.03)", borderBottom: "1px solid rgba(0,0,0,0.12)" },
  "& .MuiDataGrid-row":           { borderBottom: "1px solid rgba(0,0,0,0.06)" },
  "& .MuiDataGrid-cell":          { outline: "none" },
};
const EMPTY = {
  cnpj: "", company_name: "", code_sap: "", plant: "", active: true, last_nsu: "",
  pi_namespace: "",
};

export default function EmpresasPage() {
  const { t } = useTranslation();
  const [rows, setRows]               = useState([]);
  const [search, setSearch]           = useState("");
  const [loading, setLoading]         = useState(false);
  const [rowSelectionModel, setRowSel]= useState({ type: "include", ids: new Set() });
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(EMPTY);
  const [saving, setSaving]           = useState(false);
  const [snack, setSnack]             = useState({ open: false, msg: "", severity: "success" });

  const selectedIds = useMemo(() => Array.from(rowSelectionModel.ids), [rowSelectionModel]);
  const selectedRow = useMemo(() => rows.find(r => r.cnpj === selectedIds[0]), [rows, selectedIds]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await listCompanies()); }
    catch (e) { showSnack(e.message, "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.cnpj?.toLowerCase().includes(q) ||
      r.company_name?.toLowerCase().includes(q) ||
      r.code_sap?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const showSnack   = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  const openNew  = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = () => {
    if (!selectedRow) return;
    setEditing(selectedRow);
    setForm({ ...EMPTY, ...selectedRow });
    setDialogOpen(true);
  };
  const closeDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    if (!form.cnpj.trim()) return showSnack(t("admin.required.cnpj"), "error");
    setSaving(true);
    try {
      const cnpj = editing ? editing.cnpj : form.cnpj.trim();
      if (editing) await updateCompany(cnpj, form);
      else         await createCompany(form);
      showSnack(editing ? t("admin.success.empresaUpdated") : t("admin.success.empresaCreated"));
      closeDialog(); load();
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await Promise.all(selectedIds.map(id => deleteCompany(id)));
      showSnack(t("admin.success.empresaDeleted"));
      setDeleteOpen(false); setRowSel({ type: "include", ids: new Set() }); load();
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSaving(false); }
  };

  const columns = [
    { field: "cnpj",         headerName: t("admin.col.cnpj"),        width: 160 },
    { field: "company_name", headerName: t("admin.col.razaoSocial"), flex: 1 },
    { field: "code_sap",     headerName: t("admin.col.codSap"),      width: 110 },
    { field: "plant",        headerName: t("admin.col.centro"),      width: 100 },
    { field: "last_nsu",     headerName: t("admin.col.lastNsu"),     width: 120 },
    {
      field: "active", headerName: t("admin.col.ativaCol"), width: 80,
      renderCell: ({ value }) => <MuiSwitch checked={!!value} size="small" disableRipple sx={{ pointerEvents: "none" }} />,
    },
  ];

  const f = (field) => ({ value: form[field] ?? "", onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });

  return (
    <Box sx={{ p: 2 }}>
      <AdminPageHeader title={t("admin.pages.empresas")} />

      <AdminFilterPanel>
        <TextField label="CNPJ / Razão Social / Cód. SAP" size="small"
          value={search} onChange={e => setSearch(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, width: 320 }} />
      </AdminFilterPanel>

      <Paper variant="outlined">
        <Divider />
        <DataGrid
          rows={filtered} columns={columns} getRowId={r => r.cnpj}
          loading={loading} autoHeight checkboxSelection disableRowSelectionOnClick
          density="compact" showToolbar
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSel}
          slots={{ toolbar: AdminGridToolbar }}
          slotProps={{ toolbar: {
            title: t("admin.grid.empresas"),
            onNew: openNew, onEdit: openEdit, onDelete: () => setDeleteOpen(true),
            canEdit: selectedIds.length === 1, canDelete: selectedIds.length > 0,
          }}}
          sx={GRID_SX}
        />
      </Paper>

      <AdminFormDialog
        open={dialogOpen} onClose={closeDialog} onSave={handleSave} saving={saving}
        title={editing ? t("admin.dialog.editEmpresa") : t("admin.dialog.newEmpresa")}
      >
        <TextField label="CNPJ *" {...f("cnpj")} disabled={!!editing} size="small"
          InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 14 }} />
        <TextField label="Razão Social" {...f("company_name")} size="small" InputLabelProps={{ shrink: true }} />
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField label="Código SAP" {...f("code_sap")} size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
          <TextField label="Centro (Plant)" {...f("plant")} size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
        </Box>
        <TextField label="Último NSU" {...f("last_nsu")} size="small"
          InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 15 }}
          helperText="NSU da última distribuição DFe — deixe em branco para não alterar" />
        <FormControlLabel
          control={<Switch checked={!!form.active} onChange={e => setForm(p => ({ ...p, active: e.target.checked }))} />}
          label={t("admin.label.empresaAtiva")} />

        <Divider sx={{ my: 1 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Integração SAP PI
        </Typography>
        <TextField label="Namespace" {...f("pi_namespace")} size="small"
          InputLabelProps={{ shrink: true }} placeholder="(padrão)" />
      </AdminFormDialog>

      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 14, pb: 1, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>{t("admin.dialog.confirmDelete")}</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.6 }}>
            {t("admin.confirm.deleteEmpresa", { count: selectedIds.length })}
          </Typography>
        </DialogContent>
        <Divider sx={{ opacity: 0.5 }} />
        <DialogActions sx={{ px: 2, py: 1.2, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving} sx={{ textTransform: "none", fontSize: 13 }}>{t("admin.buttons.cancel")}</Button>
          <Button onClick={handleDelete} disabled={saving} disableElevation sx={{ textTransform: "none", fontSize: 13, color: "#d32f2f", backgroundColor: "rgba(211,47,47,0.10)", "&:hover": { backgroundColor: "rgba(211,47,47,0.18)" }, borderRadius: 0 }}>
            {saving ? t("admin.buttons.deleting") : t("admin.buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
